import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import communityApi from '../../api/communityApi';
import { ReportInterface } from "../../store/community/reportsSlice";
import { usersEntity } from "./usersActions";
import { postEntity, postsEntity } from "./postsActions";
import { UserInterface} from "../../store/community/usersSlice";
import { PostInterface } from "../../store/community/postsSlice";
import { CommentInterface } from "../../store/community/commentsSlice";
import { commentEntity, commentsEntity } from "./commentsActions";

const PREFIX = 'community';

const processStrategy = (value: any) => {
  return {
    ...value
  };
}

const reportsEntity = new schema.Entity(
  'reports',
  {
    user: usersEntity,
    active_user: usersEntity,
    post: postsEntity,
    comment: commentsEntity,
  },
  {
    processStrategy
  }
);

const reportEntity = new schema.Entity(
  'report',
  {
    user: usersEntity,
    active_user: usersEntity,
    post: postEntity,
    comment: commentEntity,
  },
  {
    processStrategy
  }
);

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      user: { [key: string]: UserInterface };
      active_user: { [key: string]: UserInterface };
      post: { [key: string]: PostInterface };
      comment: { [key: string]: CommentInterface };
      report: ReportInterface;
    }
  >(response.data.report, reportEntity);
  return normalized.entities;
}

const normalizeCollection = (response: AxiosResponse) => {
  const normalized  = normalize<
    any,
    {
      reports: { [key: string]: ReportInterface },
      users: { [key: string]: UserInterface };
      active_users: { [key: string]: UserInterface };
      posts: { [key: string]: PostInterface };
      comments: { [key: string]: CommentInterface };
    }
    >(response.data.reports, [reportsEntity]);

  return {
    reports: normalized.entities?.reports || {},
    users: normalized.entities.users || {},
    active_users: normalized.entities.active_users || {},
    posts: normalized.entities.posts || {},
    comments: normalized.entities.comments || {},
    ids: normalized.result,
    count: response.data.meta?.total_count
  };
}

export const fetchReports = createAsyncThunk(
  `${PREFIX}_reports/fetchReports`,
  async (params: object = {}) => {
    const response = await communityApi.get(`/onesky/reports`, { params })
    return normalizeCollection(response);
  }
);

export const fetchReport = createAsyncThunk(
  `${PREFIX}_reports/fetchReport`,
  async (id: number | string) => {
    const response = await communityApi.get(`/onesky/reports/${id}`);
    return normalizeInstance(response);
  }
);

export const updateReport = createAsyncThunk(
  `${PREFIX}_reports/updateReport`,
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await communityApi.patch(`/onesky/reports/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroyReport = createAsyncThunk(
  `${PREFIX}_reports/destroyReport`,
  async (id: number) => {
    await communityApi.delete(`/onesky/reports/${id}`);
    return { id: id };
  }
);
